import React from 'react';
import { compose, withState, withProps, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';

import moment from 'moment-timezone';

import { formatters as dateFormatters } from 'utils/dateAndTimes';

import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import {
  selectors as ordersSelectors,
  actions as ordersActions,
} from 'state/orders';

import RecurringOrderCard from 'components/orders/RecurringOrderCard/component';

const getRecurringOrderStatus = ({
  startFrom,
  endOn,
  startFromTimezone,
  endOnTimezone,
  t,
  rule_status,
}) => {
  const today = moment();
  const start = moment(startFrom);
  const end = moment(endOn);

  const states = [
    { status: 'cancelled', value: false },
    { status: 'active', value: today.isBetween(start, end) },
    { status: 'not_started', value: today.isBefore(start) },
    { status: 'finished', value: today.isAfter(end) },
  ];

  const orderStatus = states.find(
    (s) => rule_status?.toLowerCase() === s.status || !!s.value
  ).status;

  return t(`card.STATUS.${orderStatus}`, {
    start: dateFormatters.getHumanReadableFormattedDate({
      value: startFrom,
      timezone: startFromTimezone,
      t,
    }),
    end: dateFormatters.getHumanReadableFormattedDate({
      value: endOn,
      timezone: endOnTimezone,
      t,
    }),
  });
};

const enhance = compose(
  withTranslation('Recurring'),
  withState('opened', 'setOpened', false),
  connect(
    (state, { orderId }) => ({
      order: ordersSelectors.getOrderById(state, orderId),
    }),
    {
      openOrder: ordersActions.openOrder,
      sendEvent: analyticsActions.sendEvent,
    }
  ),
  withProps(
    ({
      order: {
        address_from: { company_name: pickupCompany, city: pickupCity },
        address_to: { company_name: dropoffCompany, city: dropoffCity },
        start_from: startFrom,
        start_time_from_tz: startFromTimezone,
        end_on: endOn,
        end_time_from_tz: endOnTimezone,
        stops,
        rule_status,
      },
      order,
      t,
    }) => {
      const nStops = stops.length - 1;
      const orderSchedule = (order && order.schedule) || [];

      const days = orderSchedule
        .map((day) => t(`weekdays_plural.${day}`))
        .join(', ');
      const schedule =
        orderSchedule.length === 7
          ? t('card.all_days')
          : t('card.schedule', { days });

      const locationsByTypeOfJourney = {
        SINGLE_SHIPMENT: (
          <Trans
            t={t}
            i18nKey="card.single_shipment_locations"
            values={{ pickupCompany, pickupCity, dropoffCompany, dropoffCity }}
            components={[<b>{pickupCompany}</b>, <b>{dropoffCompany}</b>]}
          />
        ),
        DISTRIBUTION: (
          <Trans
            t={t}
            i18nKey="card.distribution_locations"
            values={{ pickupCompany, pickupCity, nStops }}
            components={[<b>{pickupCompany}</b>, <b>{nStops}</b>]}
          />
        ),
        COLLECTION: (
          <Trans
            t={t}
            i18nKey="card.collection_locations"
            values={{ nStops, dropoffCompany, dropoffCity }}
            components={[<b>{nStops}</b>, <b>{dropoffCompany}</b>]}
          />
        ),
      };

      const status = getRecurringOrderStatus({
        startFrom,
        startFromTimezone,
        endOn,
        endOnTimezone,
        t,
        rule_status,
      });

      return {
        schedule,
        status,
        locations: locationsByTypeOfJourney[order.journey_type],
      };
    }
  ),
  withHandlers(() => ({
    openOrder:
      ({ orderId, setOpened, openOrder, opened, type, sendEvent }) =>
      () => {
        if (opened) {
          setOpened(!opened);
        } else {
          sendEvent(
            analyticsEvents.orders.list.recurringOrderConfigurationModalOpened({
              id: orderId,
            })
          );
          openOrder({ orderId, type });
        }
      },
  }))
);

export default enhance(RecurringOrderCard);
